import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import {countries, type TCountryCode} from 'countries-list'

import {type CartItem, rawCartItemSchema, transformCartItem} from './cartModel'

export enum OrderStatus {
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
  CONFIRMED = 'CONFIRMED', // initial
  CANCELLATION_REQUESTED = 'CANCELLATION_REQUESTED',
  SHIPPED = 'SHIPPED',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  COMPLETED = 'COMPLETED', // final
  CANCELLED = 'CANCELLED', // final
  RETURN_REQUESTED = 'RETURN_REQUESTED',
  RETURNED = 'RETURNED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
  REFUNDED = 'REFUNDED', // final
  FAILED = 'FAILED',
}

export const CANCELABLE_ORDER_STATUSES = [OrderStatus.CONFIRMED]

export const RETURNABLE_ORDER_STATUSES = [OrderStatus.DELIVERED]

export const NOT_BUYABLE_ORDER_STATUSES = CANCELABLE_ORDER_STATUSES

export const REBUYABLE_ORDER_STATUSES = Object.values(OrderStatus).filter(
  status => !NOT_BUYABLE_ORDER_STATUSES.includes(status),
)

export enum OrderItemStatus {
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  RETURN_REQUESTED = 'RETURN_REQUESTED',
  RETURNED = 'RETURNED',
  REFUNDED = 'REFUNDED',
  COMPLETED = 'COMPLETED',
}

const rawOrderItemSchema = Type.Composite([
  rawCartItemSchema,
  Type.Object({
    status: Type.Optional(Type.Enum(OrderItemStatus)),
    shippingTrackingNumber: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    carrier: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    retailerOrderNumber: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    orderedRetailer: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  }),
])

export type RawOrderItem = Static<typeof rawOrderItemSchema>
const rawOrderSchema = Type.Object({
  id: Type.String(),
  cartId: Type.String(),
  email: Type.String(),
  status: Type.Optional(Type.Enum(OrderStatus)),
  checkoutType: Type.String(),
  lineItems: Type.Array(rawOrderItemSchema),
  paymentInfo: Type.Object({
    amountTax: Type.Optional(Type.String()),
    checkoutSessionId: Type.Optional(Type.String()),
    paymentIntentId: Type.Optional(Type.String()),
    card: Type.Optional(
      Type.Object({
        brand: Type.String(),
        last4: Type.String(),
        expMonth: Type.Number(),
        expYear: Type.Number(),
      }),
    ),
  }),
  shippingInfo: Type.Union([
    Type.Object({
      name: Type.String(),
      line1: Type.String(),
      line2: Type.Optional(Type.String()),
      city: Type.String(),
      state: Type.String(),
      postalCode: Type.String(),
      country: Type.String(),
    }),
    Type.Object({}),
  ]),
  moreInfo: Type.Optional(
    Type.Object({
      deliveryFee: Type.Optional(Type.String()),
      estimatedDelivery: Type.Optional(Type.String()),
      amountDiscount: Type.Optional(Type.String()),
      amountRoundUp: Type.Optional(Type.String()),
    }),
  ),
  createdAt: Type.String(),
})

export type RawOrder = Static<typeof rawOrderSchema>

export const rawOrderTypeCheck = TypeCompiler.Compile(rawOrderSchema)

interface ShippingInfo {
  name: string
  line1: string
  line2?: string
  city: string
  state: string
  postalCode: string
  country: TCountryCode
}

export interface OrderItem extends CartItem {
  status?: OrderItemStatus
  shippingTrackingNumber?: string
  carrier?: string
  retailerOrderNumber?: string
  orderedRetailer?: string
}

export function transformOrderItem(rawOrderItem: RawOrderItem): OrderItem {
  return {
    ...transformCartItem(rawOrderItem),
    status: rawOrderItem.status,
    shippingTrackingNumber: rawOrderItem.shippingTrackingNumber ?? undefined,
    carrier: rawOrderItem.carrier ?? undefined,
    retailerOrderNumber: rawOrderItem.retailerOrderNumber ?? undefined,
    orderedRetailer: rawOrderItem.orderedRetailer ?? undefined,
  }
}

export function transformOrderItems(rawOrderItems: RawOrderItem[], retentive = true): OrderItem[] {
  return rawOrderItems
    .map(rawOrderItem => {
      try {
        return transformOrderItem(rawOrderItem)
      } catch (e) {
        if (!retentive) throw e
        return false
      }
    })
    .filter(Boolean)
}

export interface Order extends Omit<RawOrder, 'createdAt' | 'lineItems' | 'shippingInfo'> {
  createdAt: Date
  lineItems: OrderItem[]
  shippingInfo?: ShippingInfo
}

export function transformOrder(rawOrder: unknown): Order | false {
  if (!rawOrderTypeCheck.Check(rawOrder)) {
    console.error('Invalid order', rawOrder, ...rawOrderTypeCheck.Errors(rawOrder))
    return false
  }

  // Hide failed orders
  if (rawOrder.status === OrderStatus.FAILED) return false

  let shippingInfo: ShippingInfo | undefined

  if (Object.keys(rawOrder.shippingInfo).length > 0) {
    let shippingInfoName = ''
    let shippingInfoline1 = ''
    let shippingInfoline2: string | undefined
    let shippingInfocity = ''
    let shippingInfoState = ''
    let shippingInfoPostalCode = ''
    let shippingInfocountry: TCountryCode = 'US'

    if ('name' in rawOrder.shippingInfo) {
      shippingInfoName = rawOrder.shippingInfo.name
    }
    if ('line1' in rawOrder.shippingInfo) {
      shippingInfoline1 = rawOrder.shippingInfo.line1
    }
    if ('line2' in rawOrder.shippingInfo) {
      shippingInfoline2 = rawOrder.shippingInfo.line2
    }
    if ('city' in rawOrder.shippingInfo) {
      shippingInfocity = rawOrder.shippingInfo.city
    }
    if ('state' in rawOrder.shippingInfo) {
      shippingInfoState = rawOrder.shippingInfo.state
    }

    if ('postalCode' in rawOrder.shippingInfo) {
      shippingInfoPostalCode = String(rawOrder.shippingInfo.postalCode)
    }
    if ('country' in rawOrder.shippingInfo) {
      if (Object.keys(countries).includes(rawOrder.shippingInfo.country)) {
        shippingInfocountry = rawOrder.shippingInfo.country as TCountryCode
      } else {
        console.error('Invalid country code', rawOrder.shippingInfo.country)
        return false
      }
    }

    shippingInfo = {
      name: shippingInfoName,
      line1: shippingInfoline1,
      line2: shippingInfoline2,
      city: shippingInfocity,
      state: shippingInfoState,
      postalCode: shippingInfoPostalCode,
      country: shippingInfocountry,
    } satisfies ShippingInfo
  }

  return {
    ...rawOrder,
    shippingInfo,
    lineItems: transformOrderItems(rawOrder.lineItems),
    createdAt: new Date(rawOrder.createdAt),
  }
}
